import Button from "@shared/components/ui-kit/form/Button/Button.web";
import Flex from "@shared/components/ui-kit/layout/Flex/Flex.web";
import Box from "@shared/components/ui-kit/layout/Box/Box.web";
import Alert from "@ui-kit/feedback/alerts/Alert/Alert.web";
import CurrencyFormat from "@ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import {
  useTranslation,
  getTransComponent,
} from "@shared/utils/internationalization";
import kebabCase from "lodash/kebabCase";
import { enums, enumsTypes } from "@shared/constants";
import type { UpgradeOfferSharedProps } from "./UpgradeOffer.props";

const Trans = getTransComponent();

const FormattedPrice = ({
  value,
  currency,
}: {
  value: number;
  currency: enumsTypes.CurrencyMapIdType;
}) => (
  <CurrencyFormat
    value={value}
    currency={currency ?? enums.CURRENCY_MAP.CAD.id}
    displayType="text"
    data-testid="upgrade-offer-price"
    fixedDecimalScale={false}
    autoDecimalScale
  />
);

export default function UpgradeOffer({
  name,
  upgradeAmount,
  itemType,
  currency,
  onUpgrade,
  dataTestId,
  isLoading,
}: UpgradeOfferProps): JSX.Element {
  const { t } = useTranslation("purchases");

  return (
    <Alert
      justifyContent="space-between"
      data-testid={dataTestId ?? `upgrade-offer-alert-${kebabCase(name)}`}
    >
      <Flex alignItems="flex-start">
        <Alert.Icon
          icon="TicketStar"
          variant="solid"
          w="element.xl"
          h="element.xl"
          marginTop="2xs"
        />
        <Box>
          <Alert.Title>{t("Level up your experience!")}</Alert.Title>
          <Alert.Description>
            <Trans
              t={t}
              i18nKey="Upgrade to {{item}} for only <0>{{price}}</0> more per {{itemType}}."
              values={{
                item: name,
                itemType,
                price: upgradeAmount,
              }}
              components={[
                <FormattedPrice
                  key="price"
                  value={upgradeAmount}
                  currency={currency || "CAD"}
                />,
              ]}
            />
          </Alert.Description>
        </Box>
      </Flex>
      <Button
        ml="xs"
        data-testid={`upgrade-offer-button-${kebabCase(name)}`}
        variant="outline"
        colorPalette="purple"
        onClick={onUpgrade}
        isLoading={isLoading}
        // Ensures text doesn't overflow the button
        minWidth="fit-content"
      >
        {t("Upgrade")}
      </Button>
    </Alert>
  );
}

export type UpgradeOfferProps = UpgradeOfferSharedProps;
